import { useLayoutEffect, useRef, useState } from 'react';
import { logger } from 'features/logger/logger';
import { getAssetUrlWithTempLocationSupport } from 'features/questionnaires/store/questionnaireAssetApiSlice';

export const PROTOCOL_STYLESHEET_WAIT_MS = 1500;

const mediaFor = (active: boolean) => (active ? 'all' : 'not all');

// Keeps a link to the protocol stylesheet (`manifest.style`) in the document
// head while the calling component is mounted, applied only while `active`.
// Returns whether the stylesheet is settled: no style, loaded, failed to load,
// or waited for PROTOCOL_STYLESHEET_WAIT_MS. The cap only stops the wait: the
// link keeps downloading and the stylesheet applies when it arrives.
//
// Inactive means `media="not all"`, not `disabled`: browsers download a
// stylesheet whose media does not match (and fire `load`), but not a disabled
// one, so the stylesheet is ready the moment it becomes active. The link gets
// its media before insertion, because browsers fetch a non-matching stylesheet
// at the lowest priority. Safari loads it again on every media change; the
// asset is cached as immutable, so that read comes from the cache.
export const useProtocolStylesheet = (
  style: string | undefined,
  active: boolean
): boolean => {
  const href = style ? getAssetUrlWithTempLocationSupport(style) : undefined;
  const [settledHref, setSettledHref] = useState<string | undefined>(undefined);
  const linkRef = useRef<HTMLLinkElement | null>(null);
  const activeRef = useRef(active);

  useLayoutEffect(() => {
    activeRef.current = active;
    if (linkRef.current) linkRef.current.media = mediaFor(active);
  }, [active]);

  useLayoutEffect(() => {
    if (href === undefined) return;
    const link = document.createElement('link');
    let settled = false;
    const settle = (outcome: 'load' | 'error' | 'timeout') => {
      if (settled) return;
      settled = true;
      if (outcome === 'error') {
        logger.error('Unable to load the protocol stylesheet', {
          details: { href },
        });
      } else if (outcome === 'timeout' && activeRef.current) {
        logger.warn('The protocol stylesheet is slower than the wait cap', {
          details: { href, waitMs: PROTOCOL_STYLESHEET_WAIT_MS },
        });
      }
      setSettledHref(href);
    };
    const onLoad = () => {
      settle('load');
    };
    const onError = () => {
      settle('error');
    };
    link.addEventListener('load', onLoad);
    link.addEventListener('error', onError);
    const timer = setTimeout(() => {
      settle('timeout');
    }, PROTOCOL_STYLESHEET_WAIT_MS);
    link.rel = 'stylesheet';
    link.media = mediaFor(activeRef.current);
    link.href = href;
    document.head.appendChild(link);
    linkRef.current = link;
    return () => {
      clearTimeout(timer);
      link.removeEventListener('load', onLoad);
      link.removeEventListener('error', onError);
      link.remove();
      linkRef.current = null;
    };
  }, [href]);

  return href === undefined || settledHref === href;
};
